// Контролите на филтъра в хедъра на колоната — по един на вид филтър.
//
// Всички живеят в antd Popover/Dropdown, който ColumnMenu отваря. Ползваме
// antd за самите контроли (Input, InputNumber, RangePicker, Checkbox), защото
// така Esc стълбицата работи наготово: `hasOpenOverlay()` в SharedAccountGate
// разпознава antd слоеве по DOM и не пуска Esc към навигацията „назад".

import { useEffect, useMemo, useState } from "react";
import { Checkbox, DatePicker, Input, InputNumber } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../hooks/useViewport";
import type { ChecklistOption, ColumnFilterSpec } from "./types";

/** Под 16px iOS Safari зуумва страницата при фокус в поле. */
const inputSizeClass = (isNarrow: boolean) =>
  isNarrow ? "!text-[16px]" : "!text-[13px]";

/** Над този брой опции чеклистът получава собствена търсачка. */
const CHECKLIST_SEARCH_THRESHOLD = 6;

type Props = {
  spec: ColumnFilterSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  /** Уникалните стойности и броят им — само в клиентски режим. */
  facets?: Map<string, number> | null;
  /** Границите на колоната от фасетите — плейсхолдъри на числовия диапазон. */
  bounds?: [number, number] | null;
};

export const ColumnFilterControl = ({
  spec,
  value,
  onChange,
  facets,
  bounds,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  const sizeClass = inputSizeClass(isNarrow);

  if (spec.kind === "text") {
    return (
      <TextFilter
        value={typeof value === "string" ? value : ""}
        onChange={onChange}
        placeholder={spec.placeholder ?? t("dataTable.filterTextHint")}
        sizeClass={sizeClass}
      />
    );
  }

  if (spec.kind === "numberRange") {
    const [min, max] = Array.isArray(value)
      ? (value as [number | null, number | null])
      : [null, null];
    return (
      <div className="flex items-center gap-2">
        <InputNumber
          value={min}
          onChange={(next) => onChange([next ?? null, max])}
          placeholder={bounds ? String(bounds[0]) : t("dataTable.filterFrom")}
          className={`!w-[104px] ${sizeClass}`}
          controls={false}
        />
        <span className="text-muted">–</span>
        <InputNumber
          value={max}
          onChange={(next) => onChange([min, next ?? null])}
          placeholder={bounds ? String(bounds[1]) : t("dataTable.filterTo")}
          className={`!w-[104px] ${sizeClass}`}
          controls={false}
        />
      </div>
    );
  }

  if (spec.kind === "dateRange") {
    const [from, to] = Array.isArray(value)
      ? (value as [string | null, string | null])
      : [null, null];
    return (
      <DatePicker.RangePicker
        value={[from ? dayjs(from) : null, to ? dayjs(to) : null]}
        onChange={(range) =>
          onChange([
            range?.[0]?.startOf("day").toISOString() ?? null,
            range?.[1]?.endOf("day").toISOString() ?? null,
          ])
        }
        format="DD.MM.YYYY"
        allowEmpty={[true, true]}
        className={`!w-[248px] ${sizeClass}`}
      />
    );
  }

  return (
    <ChecklistFilter
      selected={Array.isArray(value) ? (value as string[]) : []}
      onChange={onChange}
      options={spec.options}
      facets={facets}
      sizeClass={sizeClass}
    />
  );
};

/* ────────────────────────────────── текст ───────────────────────────────── */

const TEXT_DEBOUNCE_MS = 300;

const TextFilter = ({
  value,
  onChange,
  placeholder,
  sizeClass,
}: {
  value: string;
  onChange: (value: unknown) => void;
  placeholder: string;
  sizeClass: string;
}) => {
  const [draft, setDraft] = useState(value);

  // Стойността отвън се сменя и при „Изчисти филтъра" от менюто.
  useEffect(() => setDraft(value), [value]);

  // Дебаунс, защото в сървърен режим всяка буква иначе е заявка.
  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => onChange(draft), TEXT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, value, onChange]);

  return (
    <Input
      autoFocus
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      placeholder={placeholder}
      allowClear
      className={`!w-[220px] ${sizeClass}`}
    />
  );
};

/* ───────────────────────────────── чеклист ──────────────────────────────── */

const ChecklistFilter = ({
  selected,
  onChange,
  options,
  facets,
  sizeClass,
}: {
  selected: string[];
  onChange: (value: unknown) => void;
  options?: ChecklistOption[];
  facets?: Map<string, number> | null;
  sizeClass: string;
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  // Зададени `options` = сървърен режим (фасетите там са само текущата
  // страница и списъкът би лъгал). Липсват ли — вадим ги от фасетите.
  const rows = useMemo(() => {
    if (options?.length) {
      return options.map((option) => ({
        value: option.value,
        label: option.label,
        count: facets?.get(option.value) ?? null,
      }));
    }
    const entries = Array.from(facets?.entries() ?? []);
    return entries
      .filter(([value]) => value !== "")
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
      .map(([value, count]) => ({ value, label: value, count }));
  }, [options, facets]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => row.label.toLowerCase().includes(needle));
  }, [rows, query]);

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    onChange(next.length ? next : undefined);
  };

  return (
    <div className="flex w-[236px] flex-col gap-2">
      {rows.length > CHECKLIST_SEARCH_THRESHOLD ? (
        <Input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("dataTable.filterSearch")}
          allowClear
          className={sizeClass}
        />
      ) : null}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onChange(visible.map((row) => row.value))}
          className="text-[11.5px] font-bold text-brand-deep-ink hover:underline"
        >
          {t("dataTable.filterSelectAll")}
        </button>
        <button
          type="button"
          // `undefined` МАХА филтъра. Празен масив остава в
          // `columnFilters` — фунията не свети, а филтърът виси активен.
          onClick={() => onChange(undefined)}
          disabled={!selected.length}
          className="text-[11.5px] font-bold text-muted hover:text-ink disabled:opacity-40"
        >
          {t("dataTable.filterClear")}
        </button>
      </div>

      <div className="-mx-1 max-h-[228px] overflow-y-auto px-1">
        {visible.length ? (
          <ul className="flex flex-col gap-0.5">
            {visible.map((row) => (
              <li key={row.value}>
                <label className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 hover:bg-shade-1">
                  <Checkbox
                    checked={selected.includes(row.value)}
                    onChange={() => toggle(row.value)}
                  />
                  <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-ink">
                    {row.label}
                  </span>
                  {row.count !== null ? (
                    <span className="font-pos-mono text-[11px] font-semibold tabular-nums text-faint">
                      {row.count}
                    </span>
                  ) : null}
                </label>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-1.5 py-3 text-center text-[12px] font-medium text-muted">
            {t("dataTable.filterNoValues")}
          </p>
        )}
      </div>
    </div>
  );
};
